// src/ApiPanel/pages/ApiPanelLogin.jsx
// Two-step login against the real Mokshiq API: phone + password sends an OTP,
// then the OTP is exchanged for a JWT.
//
// Deliberate choices worth keeping:
//   * The error text never distinguishes "no such account" from "wrong
//     password". The backend already returns the same "Invalid credentials"
//     for both; echoing anything more specific would turn this form into a
//     way to find out which phone numbers are registered.
//   * The OTP field is 6 digits, numeric, one-time-code autocomplete, so the
//     browser and iOS/Android can fill it from the SMS without the user
//     pasting it around.
//   * There is a resend cooldown on the client. It is a courtesy, not a
//     control — the backend's own OTP rate limit is what actually stops
//     someone hammering the endpoint.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { useApiPanelSession } from "../context/ApiPanelSessionContext";
import { API_PANEL_HOME_PATH } from "../constants/apiPanelAuth";
import "../styles/apiPanel.css";

const RESEND_SECONDS = 30;

export default function ApiPanelLogin() {
  const { requestOtp, verifyOtp, status } = useApiPanelSession();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState("credentials"); // credentials | otp
  const [form, setForm] = useState({ phone: "", password: "", otp: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const otpRef = useRef(null);

  const dest = location.state?.from?.pathname || API_PANEL_HOME_PATH;

  // Already signed in (e.g. opened /login in a second tab) — don't make them
  // authenticate again.
  useEffect(() => {
    if (status === "authed") navigate(dest, { replace: true });
  }, [status, dest, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  useEffect(() => {
    if (step === "otp") otpRef.current?.focus();
  }, [step]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const sendOtp = async (isResend = false) => {
    setError("");
    setNotice("");
    setLoading(true);
    try {
      const res = await requestOtp({ phone: form.phone.trim(), password: form.password });
      setStep("otp");
      setCooldown(RESEND_SECONDS);
      setNotice(res?.message || `We sent a verification code to ${maskPhone(form.phone)}.`);
      if (isResend) setForm((f) => ({ ...f, otp: "" }));
    } catch (err) {
      setError(err.message || "Sign-in failed. Check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCredentials = (e) => {
    e.preventDefault();
    sendOtp(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyOtp({
        phone: form.phone.trim(),
        password: form.password,
        otp: form.otp.trim(),
      });
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err.message || "That code did not work. Please try again.");
      setForm((f) => ({ ...f, otp: "" }));
      otpRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const startOver = () => {
    setStep("credentials");
    setForm((f) => ({ ...f, otp: "" }));
    setError("");
    setNotice("");
  };

  return (
    <div className="apip-login-page">
      <form
        className="apip-login-card"
        onSubmit={step === "credentials" ? handleCredentials : handleVerify}
      >
        <div className="apip-login-brand">
          <span className="apip-brand-mark">API</span>
          <span className="apip-brand-name">Panel</span>
        </div>

        <p className="apip-login-sub">
          {step === "credentials"
            ? "Sign in with your registered mobile number"
            : "Enter the verification code we sent you"}
        </p>

        <ol className="apip-steps" aria-label="Sign-in progress">
          <li className={step === "credentials" ? "active" : "done"}>
            <span>1</span> Credentials
          </li>
          <li className={step === "otp" ? "active" : ""}>
            <span>2</span> Verify
          </li>
        </ol>

        {error && (
          <div className="apip-login-error" role="alert">
            {error}
          </div>
        )}
        {notice && !error && <div className="apip-login-notice">{notice}</div>}

        {step === "credentials" ? (
          <>
            <label className="apip-field">
              <span>Mobile number</span>
              <input
                type="tel"
                required
                inputMode="numeric"
                autoComplete="username"
                pattern="[0-9]{10,15}"
                placeholder="10-digit mobile number"
                value={form.phone}
                onChange={set("phone")}
              />
            </label>

            <label className="apip-field">
              <span>Password</span>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={form.password}
                onChange={set("password")}
              />
            </label>

            <button className="apip-login-btn" type="submit" disabled={loading}>
              {loading ? "Sending code…" : "Continue"}
            </button>
          </>
        ) : (
          <>
            <div className="apip-otp-target">
              Signing in as <strong>{maskPhone(form.phone)}</strong>
              <button type="button" className="apip-linkish" onClick={startOver}>
                Change
              </button>
            </div>

            <label className="apip-field">
              <span>Verification code</span>
              <input
                ref={otpRef}
                type="text"
                required
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                pattern="[0-9]{4,6}"
                placeholder="••••••"
                className="apip-otp-input"
                value={form.otp}
                onChange={set("otp")}
              />
            </label>

            <button className="apip-login-btn" type="submit" disabled={loading}>
              {loading ? "Verifying…" : "Verify & Sign In"}
            </button>

            <button
              type="button"
              className="apip-linkish apip-resend"
              disabled={cooldown > 0 || loading}
              onClick={() => sendOtp(true)}
            >
              {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
            </button>
          </>
        )}

        <p className="apip-login-footnote">
          This sign-in is separate from your main account session.
        </p>
      </form>
    </div>
  );
}

function maskPhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (digits.length < 4) return phone || "";
  return `${"•".repeat(Math.max(digits.length - 4, 2))}${digits.slice(-4)}`;
}
